/**
 * Pure helpers for the «Velg flere» multi-select diagnose picker
 * (components/dyrejournal/diagnoseMultiPicker.js), opened from the
 * «Legg til diagnose» drawer (components/dyrejournal/addDiagnose.js).
 *
 * TICKET-01712: tick several diagnoses (e.g. DHPPi + Bb + profylakse at a
 * vaccination) and add them in one go, like the sub-product picker.
 *
 * Every function returns a new value; inputs are never mutated.
 */

// Same identity rule as «Tidligere diagnoser» in addDiagnose.js: the
// diagnose code when there is one, otherwise the diagnose id.
function sameDiagnose(a, b) {
    if (!a || !b) {
        return false;
    }
    if (b.diagnoseCode) {
        return a.diagnoseCode === b.diagnoseCode;
    }
    return a.id === b.id;
}

/**
 * 3 or fewer digits narrow the hits to diagnose codes starting with them;
 * anything else keeps the wider backend search as-is.
 */
export function filterDiagnoseResults(results, search) {
    const list = Array.isArray(results) ? results : [];
    if (search && search.length <= 3 && /^\d+$/.test(search)) {
        return list.filter(d => d.diagnoseCode && d.diagnoseCode.startsWith(search));
    }
    return list;
}

/**
 * @param {Array} journalDiagnoses - the journal's current diagnoses (may be undefined)
 * @param {object} diagnose - a search hit
 * @returns {boolean} true when the journal already has this diagnose
 */
export function isDiagnoseOnJournal(journalDiagnoses, diagnose) {
    if (!Array.isArray(journalDiagnoses)) {
        return false;
    }
    return journalDiagnoses.some(d => sameDiagnose(d, diagnose));
}

/** Add the diagnose to the selection, or remove it when already selected. */
export function toggleDiagnoseSelection(selected, diagnose) {
    const current = Array.isArray(selected) ? selected : [];
    if (current.some(d => sameDiagnose(d, diagnose))) {
        return current.filter(d => !sameDiagnose(d, diagnose));
    }
    return [...current, diagnose];
}

/** Remove one diagnose from the selection. */
export function removeDiagnoseSelection(selected, diagnose) {
    const current = Array.isArray(selected) ? selected : [];
    return current.filter(d => !sameDiagnose(d, diagnose));
}

/**
 * The journal diagnose entry for one picked diagnose: the same fields as the
 * single-pick searchDiagnoseCallback in addDiagnose.js. `differential` is the
 * Number 1, never a Boolean (backend/lib/reportable-diagnoses.js).
 */
export function buildInputDiagnose(diagnose, { differential = false } = {}) {
    const inputDiagnose = {
        id: diagnose.id,
        name: diagnose.name,
        diagnoseCode: diagnose.diagnoseCode,
        uuidv4: diagnose.uuidv4,
        speciesAllowed: diagnose.speciesAllowed,
    };
    if (differential) {
        inputDiagnose.differential = 1;
    }
    return inputDiagnose;
}

/**
 * Save the selected diagnoses one at a time. Sequential on purpose: each save
 * PATCHes one $addToSet and updates the shared journal object before the next
 * one reads it. Stops at the first failure.
 *
 * @returns {Promise<{added: number, remaining: Array, error: (Error|null)}>}
 */
export async function addDiagnosesSequentially(selected, saveOne, onProgress = () => {}) {
    const list = Array.isArray(selected) ? selected : [];
    for (let i = 0; i < list.length; i += 1) {
        try {
            // eslint-disable-next-line no-await-in-loop
            await saveOne(list[i]);
        } catch (error) {
            return { added: i, remaining: list.slice(i), error };
        }
        onProgress(i + 1);
    }
    return { added: list.length, remaining: [], error: null };
}
